import { FC } from "react";
import { Navigate, Outlet, useLocation } from "react-router-dom";
import { AppRouteUrls } from "../../../constants";
import { useOnboardingState } from "../hooks/useOnboardingState";

/**
 * Sends users who started onboarding but never finished it back into the wizard.
 *
 * This is what makes the flow resumable: previously the wizard was reachable only from the
 * magic-link registration redirect, so closing the tab mid-flow left the account
 * half-configured with nothing ever bringing the user back.
 *
 * Three deliberate non-redirect cases:
 *  - while the query is loading, so the app doesn't flash the wizard on every cold start;
 *  - when the query errored, so a failing endpoint can't lock users out of the product;
 *  - when `hasStarted` is false, which is every account created before this feature shipped.
 */
export const OnboardingGuard: FC = () => {
  const location = useLocation();
  const { data, isLoading, isError } = useOnboardingState();

  const alreadyOnWizard = location.pathname === AppRouteUrls.Onboarding;
  const shouldResume = data?.hasStarted === true && !data.isComplete;

  if (!isLoading && !isError && shouldResume && !alreadyOnWizard) {
    return <Navigate to={AppRouteUrls.Onboarding} replace />;
  }

  return <Outlet />;
};
